// ═══════════════════════════════════════════════════════════════
// DonorDetailView — Classic Layout
// Path: src/components/donorDetail/DonorDetailClassic.jsx
//
// Extracted from DonorDetailView.jsx during modularization Phase 4.
// Single-column AccentSection stack with CollapsibleSections for
// lower-priority data. Original layout preserved pixel-for-pixel.
//
// Props-only — does not call useApp() directly.
// ═══════════════════════════════════════════════════════════════

import React from "react";
import {
  User,
  CreditCard,
  MapPin,
  Briefcase,
  Tag,
  FileText,
  MessageSquare,
  Send,
  Layers,
  Sprout,
  Gift,
  Film,
  Megaphone,
  Notebook,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { MEMBER_TYPES, CFRE_OPTIONS } from "../../constants/memberTypes";
import { FormInput } from "../ui/FormControls";
import { GiftsSection } from "../features/GiftsSection";
import { MemberSponsorshipsSection } from "../shows/MemberSponsorshipsSection";
import { Field, AccentSection, CollapsibleSection } from "./DonorDetailShared";
import {
  ClassificationControls,
  ContactPreferencesCompact,
} from "./ClassificationSection";
import { CultivationContent } from "./CultivationSection";
import { CommunicationContent } from "./CommunicationSection";
import { CampaignsSection } from "./CampaignsSection";
import { CustomFieldsContent } from "./CustomFieldsSection";
import { AttachmentList } from "./AttachmentList";
import { RelationshipsPanel } from "./RelationshipsPanel";
import { ContactReportsSection } from "./ContactReportsSection";

// ═══════════════════════════════════════════════════════════════
// DonorDetailClassic
// ═══════════════════════════════════════════════════════════════

export const DonorDetailClassic = ({
  // Data
  detailForm,
  selectedMember,
  palette,
  effective,
  // Cultivation data
  engagement,
  engPathway,
  scoreColor,
  daysColor,
  scorePct,
  selectedPersona,
  suggestion,
  // Permissions
  canEdit,
  canLogContact,
  canViewGifts,
  canFileContactReports,
  // Handlers
  updateField,
  updateMember,
  handleGiftSaved,
  // Modal triggers
  onLogContact,
  onEditLog,
  onDeleteLog,
  // Context data
  pathways,
  personas,
  boardMembers,
  members,
  customFields,
  // Classic editing (global edit mode)
  editing,
  fieldProps,
  // Messaging
  showMessage,
  // Navigation
  navigate,
}) => (
  <div
    style={{
      flex: 1,
      padding: 24,
      maxWidth: 900,
      margin: "0 auto",
      width: "100%",
      overflowY: "auto",
    }}
  >
    {/* ── Classification & Assignment ───────────────────────────── */}
    <AccentSection
      title="Classification & Assignment"
      icon={Tag}
      color={effective.color}
    >
      <ClassificationControls
        detailForm={detailForm}
        canEdit={canEdit}
        updateField={updateField}
        updateMember={updateMember}
        selectedMember={selectedMember}
        pathways={pathways}
        personas={personas}
        boardMembers={boardMembers}
        members={members}
        effective={effective}
        palette={palette}
      />
      {/* Contact Preferences (v1.3.0) */}
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: `1px solid ${palette.cardBorder}`,
        }}
      >
        <ContactPreferencesCompact
          detailForm={detailForm}
          canEdit={canEdit}
          updateField={updateField}
          updateMember={updateMember}
          selectedMember={selectedMember}
          palette={palette}
        />
      </div>
      {/* Relationships */}
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: `1px solid ${palette.cardBorder}`,
        }}
      >
        <RelationshipsPanel
          selectedMember={selectedMember}
          members={members}
          canEdit={canEdit}
          palette={palette}
          navigate={navigate}
          showMessage={showMessage}
        />
      </div>
    </AccentSection>

    {/* ── Notes & Intelligence ──────────────────────────────────── */}
    <AccentSection
      title="Notes & Intelligence"
      icon={FileText}
      color={effective.color}
    >
      <textarea
        value={detailForm.notes || ""}
        onChange={(e) => {
          if (canEdit) updateField("notes", e.target.value);
        }}
        onBlur={() => {
          if (canEdit)
            updateMember(selectedMember.id, { notes: detailForm.notes }, true);
        }}
        rows={4}
        readOnly={!canEdit}
        placeholder={
          canEdit
            ? "What makes this member tick? Interests, motivations, engagement preferences..."
            : ""
        }
        style={{
          width: "100%",
          padding: "12px 14px",
          borderRadius: 8,
          fontSize: FONT_SIZES.md,
          fontFamily: FONT_FAMILY,
          outline: "none",
          background: canEdit ? palette.inputBg : `${palette.inputBg}80`,
          color: palette.inputColor,
          border: `1px solid ${palette.inputBorder}`,
          resize: canEdit ? "vertical" : "none",
          transition: "border-color 0.2s",
          opacity: canEdit ? 1 : 0.7,
          boxSizing: "border-box",
        }}
        onFocus={(e) => {
          if (canEdit) {
            e.target.style.borderColor = effective.color;
            e.target.style.boxShadow = `0 0 0 3px ${effective.color}20`;
          }
        }}
      />
      <AttachmentList
        memberId={selectedMember?.id}
        attachments={detailForm.attachments || []}
        canEdit={canEdit}
        palette={palette}
        onAttachmentsChange={(updated) => {
          updateField("attachments", updated);
          updateMember(selectedMember.id, { attachments: updated }, true);
        }}
        showMessage={showMessage}
      />
    </AccentSection>

    {/* ── Cultivation ──────────────────────────────────────────── */}
    <AccentSection title="Cultivation" icon={Sprout} color="#10b981">
      <CultivationContent
        engagement={engagement}
        engPathway={engPathway}
        scoreColor={scoreColor}
        daysColor={daysColor}
        scorePct={scorePct}
        selectedPersona={selectedPersona}
        suggestion={suggestion}
        detailForm={detailForm}
        canEdit={canEdit}
        updateField={updateField}
        updateMember={updateMember}
        selectedMember={selectedMember}
        palette={palette}
      />
    </AccentSection>

    {/* ── Communication History ─────────────────────────────────── */}
    <AccentSection
      title="Communication History"
      icon={MessageSquare}
      color={effective.color}
    >
      <CommunicationContent
        contactLog={detailForm.contactLog || []}
        canLogContact={canLogContact}
        onLogContact={onLogContact}
        onEditLog={onEditLog}
        onDeleteLog={onDeleteLog}
        palette={palette}
      />
    </AccentSection>

    {/* ── Contact Reports ──────────────────────────────────────── */}
    {canFileContactReports && (
      <AccentSection
        title="Contact Reports"
        icon={Notebook}
        color={effective.color}
      >
        <ContactReportsSection
          member={selectedMember}
          palette={palette}
          canEdit={canEdit}
        />
      </AccentSection>
    )}

    {/* ── Gift History ─────────────────────────────────────────── */}
    {canViewGifts && (
      <AccentSection title="Gift History" icon={Gift} color={effective.color}>
        <GiftsSection
          member={selectedMember}
          palette={palette}
          canEdit={canEdit}
          onGiftSaved={handleGiftSaved}
        />
      </AccentSection>
    )}

    {/* ── Campaigns ─────────────────────────────────────────────── */}
    {canViewGifts && (
      <AccentSection title="Campaigns" icon={Megaphone} color={effective.color}>
        <CampaignsSection
          member={selectedMember}
          palette={palette}
        />
      </AccentSection>
    )}

    {/* ── Show Sponsorships (Phase 7) ────────────────────────── */}
    <AccentSection title="Show Sponsorships" icon={Film} color={effective.color}>
      <MemberSponsorshipsSection
        member={selectedMember}
        palette={palette}
        canEdit={canEdit}
        canViewGifts={canViewGifts}
      />
    </AccentSection>

    {/* ── Custom Fields ────────────────────────────────────────── */}
    {customFields.length > 0 && (
      <AccentSection
        title="Custom Fields"
        icon={Layers}
        color={effective.color}
      >
        <CustomFieldsContent
          customFields={customFields}
          detailForm={detailForm}
          updateField={updateField}
          updateMember={updateMember}
          selectedMember={selectedMember}
          palette={palette}
        />
      </AccentSection>
    )}

    {/* ── Personal Information ──────────────────────────────────── */}
    <CollapsibleSection
      title="Personal Information"
      icon={User}
      palette={palette}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {(detailForm.donorType || "individual") === "organization" ? (
          <>
            <Field label="Organization Name" field="organization" {...fieldProps} />
            <Field label="Title" field="title" {...fieldProps} />
            <Field label="Contact First Name" field="firstName" {...fieldProps} />
            <Field label="Contact Last Name" field="lastName" {...fieldProps} />
          </>
        ) : (
          <>
            <Field label="First Name" field="firstName" {...fieldProps} />
            <Field label="Last Name" field="lastName" {...fieldProps} />
            <Field label="Credentialed Name" field="credentialedFullName" {...fieldProps} />
            <Field label="Title" field="title" {...fieldProps} />
          </>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 600,
              color: palette.text2,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Email
          </span>
          {editing ? (
            <FormInput
              value={detailForm.email || ""}
              onChange={(v) => updateField("email", v)}
              type="email"
            />
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontSize: FONT_SIZES.md,
                  color: detailForm.email ? palette.text1 : palette.text3,
                }}
              >
                {detailForm.email || "\u2014"}
              </span>
              {detailForm.email && (
                <button
                  onClick={onLogContact}
                  title="Send email & log contact"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: "1px solid #3b82f6",
                    background: "#eff6ff",
                    color: "#3b82f6",
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#3b82f6";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#eff6ff";
                    e.currentTarget.style.color = "#3b82f6";
                  }}
                >
                  <Send size={ICON_SIZES.xs} /> Email
                </button>
              )}
            </div>
          )}
        </div>
        <Field label="Phone" field="phone" {...fieldProps} />
        <Field label="Birthdate" field="birthdate" {...fieldProps} />
        <Field label="Website" field="website" {...fieldProps} />
      </div>
    </CollapsibleSection>

    {/* ── Professional ─────────────────────────────────────────── */}
    <CollapsibleSection title="Professional" icon={Briefcase} palette={palette}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Organization" field="organization" {...fieldProps} />
        <Field
          label="Years of Experience"
          field="yearsExperience"
          {...fieldProps}
        />
        <Field label="Designation" field="designation" {...fieldProps} />
      </div>
    </CollapsibleSection>

    {/* ── Donor Details ────────────────────────────────────────── */}
    <CollapsibleSection
      title="Donor Details"
      icon={CreditCard}
      muted
      palette={palette}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 16,
        }}
      >
        <Field label="Spektrix ID" field="spektrixId" {...fieldProps} />
        <Field label="CP Donor ID" field="cpDonorId" {...fieldProps} />
        <Field label="Donor Since" field="memberSince" {...fieldProps} />
        <Field label="Start Date" field="startDate" {...fieldProps} />
        <Field label="End Date" field="endDate" {...fieldProps} />
        <Field
          label="Org Pays Dues"
          field="orgPaysDues"
          options={["Yes", "No"]}
          {...fieldProps}
        />
        <Field
          label="Auto-Renews"
          field="autoRenews"
          options={["Yes", "No"]}
          {...fieldProps}
        />
        <Field
          label="Complimentary Reason"
          field="complimentaryReason"
          {...fieldProps}
        />
      </div>
    </CollapsibleSection>

    {/* ── Address ──────────────────────────────────────────────── */}
    <CollapsibleSection title="Address" icon={MapPin} muted palette={palette}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Street" field="homeStreet" {...fieldProps} />
        <Field label="City" field="homeCity" {...fieldProps} />
        <Field label="State/Province" field="homeState" {...fieldProps} />
        <Field label="Zip/Postal Code" field="homeZip" {...fieldProps} />
        <Field label="Country" field="homeCountry" {...fieldProps} />
      </div>
    </CollapsibleSection>
  </div>
);
